/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { BlogCardsSectionFragment } from "@cosmos/hygraph-schema";
import { ResponsiveContainer, SectionContainer, SectionHeading, BackgroundContainer } from "@components/elements";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";
import { BlogCards } from "./BlogCards.organism";

export interface BlogCardsSectionProps extends Omit<BlogCardsSectionFragment, "__typename" | "id"> {
	sectionIndex?: number;
	hideCategoryPills?: boolean;
}

// Helper function to convert enum values to numeric values
const convertMaxSizeToNumber = (maxSize: string | null | undefined): 1 | 2 | 3 | 4 => {
	switch (maxSize) {
		case "two":
			return 2;
		case "three":
			return 3;
		case "four":
			return 4;
		default:
			return 3; // Default fallback
	}
};

export const BlogCardsSection = ({
	config,
	cards,
	maxSize,
	hideCategoryPills,
}: BlogCardsSectionProps): React.JSX.Element | null => {
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(cards?.length);
	if (!validHeading && !validContent) return null;
	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer disablePaddingBlockEnd={!!config?.disablePaddingBottom}>
					<VStack
						alignItems="stretch"
						gap="eds.dimension.48"
						className={cx("blog-cards-section", css({ smDown: { gap: "eds.dimension.40" } }))}
					>
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<BlogCards
								cards={cards || []}
								maxRowSize={convertMaxSizeToNumber(maxSize)}
								hideCategoryPills={hideCategoryPills}
							/>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
